import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../utils/authentication";
import { useTheme } from "../theme/theme-provider";
import { motion, AnimatePresence } from "framer-motion";
import { BsImages, BsSend, BsThreeDots, BsTypeH5 } from "react-icons/bs";
import { BiLogOut, BiUser, BiBot } from "react-icons/bi";
import { HiOutlineSparkles } from "react-icons/hi2";
import ReactMarkdown from 'react-markdown';

function ChatPage() {
    const theme = useTheme();
    const [messages, setMessages] = useState([
        { id: Date.now(), text: "Hello! How can I help you today?", sender: "other" },
    ]);
    const [newMessage, setNewMessage] = useState("");
    const [showImages, setShowImages] = useState(true);
    const socket = useRef(null);
    const hasConnected = useRef(false);
    const [isConnected, setIsConnected] = useState(false);
    const [isInputEnabled, setIsInputEnabled] = useState(false);
    const [chatState, setChatState] = useState("initial");
    const wsUrl = process.env.REACT_APP_WS_URL;
    const chatContainerRef = useRef(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [isButtonDisplayed, setIsButtonDisplayed] = useState(false);
    const cookieCheckIntervalRef = useRef(null);
    const [showScrollToBottomButton, setShowScrollToBottomButton] = useState(false);

    const liveRegionRef = useRef(null); //for screen readers

    const announceMessage = (text) => {
        if (!liveRegionRef.current) return;
        liveRegionRef.current.textContent = text;
    };

    useEffect(() => {
        if (hasConnected.current) return;
        hasConnected.current = true;

        // const userId = localStorage.getItem("userId");
        const userId = "chetan@112233";
        socket.current = new WebSocket(`${wsUrl}/ws/chat/${userId}`);

        socket.current.onopen = () => {
            console.log("Connected to chat!");
            setIsConnected(true);
            setIsInputEnabled(true);
            setChatState("initial");
        };

        socket.current.onmessage = (event) => {
            const data = JSON.parse(event.data);

            if (data.type === "status") {
                switch (data.status) {
                    case "processing":
                        setChatState("processing");
                        announceMessage("Please wait while your message is processed.");
                        break;
                    case "ready":
                        setIsInputEnabled(true);
                        setChatState("end_turn");
                        // Reintroduce a delay for "Waiting for your reply" to allow the main message to be spoken
                        setTimeout(() => {
                            announceMessage("Waiting for your reply.");
                        }, 1000); // Increased delay to 1000ms (1 second)
                        break;
                    default:
                        break;
                }
            } else if (data.type === "message") {
                const newMsg = data.content;
                setMessages((prev) => [
                    ...prev,
                    { id: Date.now(), text: data.content, sender: "other" },
                ]);
                announceMessage(newMsg);
            } else if (data.type === "image") {
                setMessages((prev) => [
                    ...prev,
                    {
                        id: Date.now(),
                        text: data.content,
                        sender: "other",
                        isImage: true,
                    },
                ]);
                // Announce "Image received." when an image message arrives.
                announceMessage("Image received.");
            } else if (data.type === "cookie-action") {
                if (data.content === "get-linkedin-cookies") {
                    handleCookieExtraction({
                        domainName: "LinkedIn",
                        domains: ["www.linkedin.com", ".linkedin.com"],
                        cookieName: "li_at",
                        successMessage:
                            "Successfully logged into your LinkedIn account.",
                        buttonText: "Open LinkedIn",
                        buttonLink: "https://www.linkedin.com",
                        socketMessageType: "linkedin-cookies",
                    });
                } else if (data.content === "get-naukri-cookies") {
                    handleCookieExtraction({
                        domainName: "Naukri",
                        domains: ["www.naukri.com", ".naukri.com"],
                        cookieName: "nauk_at",
                        successMessage:
                            "Successfully logged into your Naukri account.",
                        buttonText: "Open Naukri",
                        buttonLink: "https://www.naukri.com",
                        socketMessageType: "naukri-cookies",
                    });
                } else if (data.content === "get-swiggy-cookies") {
                    handleCookieExtraction({
                        domainName: "Swiggy",
                        domains: ["www.swiggy.com", ".swiggy.com"],
                        cookieName: "_is_logged_in",
                        successMessage:
                            "Successfully logged into your Swiggy account.",
                        buttonText: "Open Swiggy",
                        buttonLink: "https://www.swiggy.com",
                        socketMessageType: "swiggy-cookies",
                    });
                } else if (data.content === "get-uber-cookies") {
                    handleCookieExtraction({
                        domainName: "Uber",
                        domains: ["m.uber.com", ".uber.com", "www.uber.com"],
                        cookieName: "_ua",
                        successMessage:
                            "Successfully logged into your Uber account.",
                        buttonText: "Open Uber",
                        buttonLink: "https://www.uber.com",
                        socketMessageType: "uber-cookies",
                    });
                } else if (data.content === "get-1mg-cookies") {
                    handleCookieExtraction({
                        domainName: "1mg",
                        domains: [".1mg.com", "www.1mg.com"],
                        cookieName: "VISITOR-ID",
                        successMessage:
                            "Successfully logged into your 1mg account.",
                        buttonText: "Open 1mg",
                        buttonLink: "https://www.1mg.com",
                        socketMessageType: "1mg-cookies",
                    });
                }
            }
        };

        socket.current.onclose = (event) => {
            console.log("Disconnected from chat", event);
            setIsConnected(false);
            setIsInputEnabled(false);
            setChatState("initial");
            hasConnected.current = false;
        };

        return () => {
            if (
                socket.current &&
                socket.current.readyState === WebSocket.OPEN
            ) {
                socket.current.close();
                setIsConnected(false);
                hasConnected.current = false;
            }
        };
    }, []);

    const handleCookieExtraction = ({
        domainName,
        domains,
        cookieName,
        successMessage,
        buttonText,
        buttonLink,
        socketMessageType,
    }) => {
        const extensionId = "aeaachdmgnjpbihcafbkkpjmkjbkonjh";

        const checkCookies = () => {
            if (window.chrome?.runtime) {
                window.chrome.runtime.sendMessage(
                    extensionId,
                    { action: "fetchCookies", domains },
                    (response) => {
                        if (window.chrome.runtime.lastError) {
                            console.error(
                                `Error fetching cookies for ${domainName}:`,
                                window.chrome.runtime.lastError.message
                            );
                        } else if (response && response.cookies) {
                            console.log(
                                `${domainName} cookies:`,
                                response.cookies
                            );
                            const requiredCookie = response.cookies.find(
                                (cookie) => cookie.name === cookieName
                            );

                            if (requiredCookie) {
                                console.log(
                                    `Required cookies found for ${domainName}`
                                );

                                if (
                                    socket.current &&
                                    socket.current.readyState === WebSocket.OPEN
                                ) {
                                    socket.current.send(
                                        JSON.stringify({
                                            type: socketMessageType,
                                            text: response.cookies,
                                        })
                                    );
                                }

                                setMessages((prev) => [
                                    ...prev.filter((msg) => !msg.isButton),
                                    {
                                        id: Date.now(),
                                        text: successMessage,
                                        sender: "other",
                                    },
                                ]);
                                setIsButtonDisplayed(false);
                                if (cookieCheckIntervalRef.current) {
                                    clearInterval(
                                        cookieCheckIntervalRef.current
                                    );
                                    cookieCheckIntervalRef.current = null;
                                }
                            } else if (!isButtonDisplayed) {
                                console.log(
                                    `Required cookies missing for ${domainName}. Sending button to user.`
                                );
                                setMessages((prev) => [
                                    ...prev.filter((msg) => !msg.isButton),
                                    {
                                        id: Date.now(),
                                        text: `To complete the task, I need access to your ${domainName} account. Please click the button below to open ${domainName} and log in to your account.`,
                                        sender: "other",
                                        isButton: true,
                                        buttonText,
                                        buttonLink,
                                    },
                                ]);
                                setIsButtonDisplayed(true);
                            }
                        }
                    }
                );
            } else {
                console.error("Chrome runtime API not available.");
            }
        };

        // Perform initial check
        checkCookies();

        // Start polling every 5 seconds if not already polling
        if (!cookieCheckIntervalRef.current) {
            cookieCheckIntervalRef.current = setInterval(checkCookies, 5000);
        }

        // Update the socket onclose handler to clear the interval
        const originalOnClose = socket.current.onclose;
        socket.current.onclose = (event) => {
            if (cookieCheckIntervalRef.current) {
                clearInterval(cookieCheckIntervalRef.current);
                cookieCheckIntervalRef.current = null;
            }
            if (originalOnClose) {
                originalOnClose(event);
            }
        };
    };

    // Clean up interval on component unmount
    useEffect(() => {
        return () => {
            if (cookieCheckIntervalRef.current) {
                clearInterval(cookieCheckIntervalRef.current);
                cookieCheckIntervalRef.current = null;
            }
        };
    }, []);

    // Announce initial message once component mounts and live region is ready
    useEffect(() => {
        if (liveRegionRef.current && messages.length === 1 && messages[0].text === "Hello! How can I help you today?") {
            announceMessage(messages[0].text);
        }
    }, [liveRegionRef.current, messages]); // Depend on liveRegionRef.current and messages

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop =
                chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    useEffect(() => {
        if (isInputEnabled && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isInputEnabled]);

    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };

    const handleScroll = () => {
        if (chatContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
            const isAtBottom = scrollHeight - scrollTop <= clientHeight + 1; // +1 for slight tolerance
            setShowScrollToBottomButton(!isAtBottom);
        }
    };

    useEffect(() => {
        const chatContainer = chatContainerRef.current;
        if (chatContainer) {
            chatContainer.addEventListener("scroll", handleScroll);
            return () => {
                chatContainer.removeEventListener("scroll", handleScroll);
            };
        }
    }, []); // Empty dependency array means this runs once on mount and cleanup on unmount

    const handleSendMessage = () => {
        if (
            newMessage.trim() &&
            socket.current?.readyState === WebSocket.OPEN
        ) {
            setMessages((prev) => [
                ...prev,
                { id: Date.now(), text: newMessage, sender: "user" },
            ]);

            socket.current.send(
                JSON.stringify({
                    text: newMessage,
                    type: chatState === "initial" ? "initial" : "continuation",
                })
            );

            setNewMessage("");
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleLogout = () => {
        logout();
    };

    const toggleImageMessages = () => {
        setShowImages((prevState) => !prevState);
    };

    return (
        <div className="flex flex-col h-screen bg-white dark:bg-black transition-all duration-500">
            {/* Header */}
            <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-between p-4 bg-white dark:bg-black border-b border-gray-300 dark:border-gray-700 shadow-sm"
            >
                <div className="flex items-center space-x-3">
                    <div className="relative">
                        <div className="w-10 h-10 bg-black dark:bg-white rounded-full flex items-center justify-center">
                            <HiOutlineSparkles className="w-5 h-5 text-white dark:text-black" />
                        </div>
                        {isConnected && (
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-black dark:bg-white rounded-full border-2 border-gray-200 dark:border-gray-800 animate-pulse"></div>
                        )}
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-black dark:text-white">I-Stem Assistant</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            {isConnected ? "Online" : "Connecting..."}
                        </p>
                    </div>
                </div>
                <div className="flex items-center space-x-3">
                    <motion.button
                        onClick={toggleImageMessages}
                        className={`
                            flex items-center gap-2 px-4 py-2 rounded-lg border
                            ${showImages
                                ? "bg-black dark:bg-white text-white dark:text-black border-gray-300 dark:border-gray-700"
                                : "bg-white dark:bg-black text-black dark:text-white border-gray-300 dark:border-gray-700"
                            }
                            hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black
                            transition-all duration-200
                        `}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title={showImages ? "Hide images" : "Show images"}
                    >
                        <BsImages className="w-4 h-4" />
                        <span className="text-sm font-medium">
                            {showImages ? "Hide Images" : "Show Images"}
                        </span>
                    </motion.button>
                    <motion.button
                        onClick={handleLogout}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg border bg-white dark:bg-black text-black dark:text-white border-gray-300 dark:border-gray-700 hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all duration-200"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Logout"
                    >
                        <BiLogOut className="w-4 h-4" />
                        <span className="text-sm font-medium">Logout</span>
                    </motion.button>
                </div>
            </motion.div>

            {/* SCREEN READER LIVE REGION */}
            <div
                ref={liveRegionRef}
                aria-live="assertive"
                aria-atomic="true"
                className="sr-only"
            />

            {/* Main Chat Area */}
            <div
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto p-4 space-y-4 scroll-smooth"
                role="log"
                aria-live="polite"
            >
                <AnimatePresence>
                    {messages.map(
                        (message) =>
                            !(message.isImage && !showImages) && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                                    transition={{ duration: 0.4, ease: "easeOut" }}
                                    key={message.id}
                                    tabIndex="0" // Make message focusable
                                    className={`flex items-start gap-3 ${message.sender === "user"
                                        ? "justify-end"
                                        : "justify-start"
                                        }`}
                                >
                                    {/* Avatar for bot messages */}
                                    {message.sender === "other" && (
                                        <motion.div 
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ delay: 0.2 }}
                                            className="w-8 h-8 bg-black dark:bg-white rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                                        >
                                            <BiBot className="w-4 h-4 text-white dark:text-black" aria-hidden="true" />
                                        </motion.div>
                                    )}

                                    <div
                                        className={`
                                        max-w-md rounded-2xl shadow-sm
                                        ${message.sender === "user"
                                                ? "bg-black dark:bg-white text-white dark:text-black ml-auto"
                                                : "bg-white dark:bg-black text-black dark:text-white border border-gray-300 dark:border-gray-700"
                                            }
                                        ${message.isImage ? "p-2" : "px-4 py-3"}
                                        transform transition-all duration-300
                                        hover:shadow-md hover:scale-[1.01]
                                    `}
                                    >
                                        {message.isButton ? (
                                            <div className="space-y-3">
                                                {message.sender === "user" ? 
                                                (
                                                    <h2 className="text-sm font-semibold text-right text-black">
                                                        You
                                                    </h2>
                                                )
                                                :
(
                                                    <h2 className="text-sm font-semibold text-left text-white">
                                                        Agent
                                                    </h2>
                                                )                                                    
                                                }
                                                {/* <p className="text-sm leading-relaxed">
                                                    {message.text}
                                                </p> */}
                                                <ReactMarkdown 
                                                    components={{
                                                        p: ({ node, ...props }) => <p {...props} className="text-sm leading-relaxed" />
                                                    }}
                                                >
                                                 {message.text}
                                                </ReactMarkdown>
                                                <motion.button
                                                    onClick={() =>
                                                        window.open(
                                                            message.buttonLink,
                                                            "_blank"
                                                        )
                                                    }
                                                    className="w-full px-4 py-2 bg-black dark:bg-white border border-gray-300 dark:border-gray-700
                                                         text-white dark:text-black rounded-lg hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white
                                                         transition-all duration-200 font-medium
                                                         focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-600
                                                         focus:ring-offset-2 dark:focus:ring-offset-black
                                                         shadow-sm hover:shadow-md"
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    {message.buttonText}
                                                </motion.button>
                                            </div>
                                        ) : message.isImage && showImages ? (
                                            <motion.img
                                                src={message.text}
                                                alt="Image received in chat"
                                                className="w-full h-auto rounded-lg"
                                                loading="lazy"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 0.5 }}
                                            />
                                        ) : (
                                            <div className="space-y-2">
                                                    {message.sender === "user" ? 
                                                    (
                                                        <h2 className="text-sm font-semibold text-right text-black">
                                                            You
                                                        </h2>
                                                    )
                                                    :
 (
                                                        <h2 className="text-sm font-semibold text-left text-white">
                                                            Agent
                                                        </h2>
                                                    )                                                    
                                                    }
                                                {/* <p className="text-sm leading-relaxed">
                                                    {message.text}
                                                </p> */}
                                                <ReactMarkdown 
                                                    components={{
                                                        p: ({ node, ...props }) => <p {...props} className="text-sm leading-relaxed" />
                                                    }}
                                                >
                                                 {message.text}
                                                </ReactMarkdown>
                                                {/* <div className="flex items-center justify-between">
                                                    <span className="text-xs opacity-70">
                                                        {new Date(
                                                            message.id
                                                        ).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                    {message.sender === "user" && (
                                                        <BiUser className="w-3 h-3 opacity-70" />
                                                    )}
                                                </div> */}
                                            </div>
                                        )}
                                    </div>

                                    {/* Avatar for user messages */}
                                    {message.sender === "user" && (
                                        <motion.div 
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ delay: 0.2 }}
                                            className="w-8 h-8 bg-black dark:bg-white rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                                        >
                                            <BiUser className="w-4 h-4 text-white dark:text-black" aria-hidden="true" />
                                        </motion.div>
                                    )}
                                </motion.div>
                            )
                    )}
                </AnimatePresence>

                {/* Scroll to Bottom Button */}
                <AnimatePresence>
                    {showScrollToBottomButton && (
                        <motion.button
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 50 }}
                            transition={{ duration: 0.3 }}
                            onClick={scrollToBottom}
                            className="fixed bottom-24 right-8 p-3 rounded-full bg-black dark:bg-white text-white dark:text-black shadow-lg hover:scale-105 transition-all duration-200 z-50"
                            aria-label="Scroll to latest message"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" />
                            </svg>
                        </motion.button>
                    )}
                </AnimatePresence>

                {/* Typing indicator */}
                {chatState === "processing" && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="flex items-start gap-3"
                        role="status"
                        aria-live="polite"
                    >
                        <div className="w-8 h-8 bg-black dark:bg-white rounded-full flex items-center justify-center flex-shrink-0">
                            <BiBot className="w-4 h-4 text-white dark:text-black" />
                        </div>
                        <div className="bg-white dark:bg-black rounded-2xl px-4 py-3 shadow-sm border border-gray-300 dark:border-gray-700">
                            <div className="flex flex-col items-start space-y-1">
                                <h2 className="text-sm font-semibold text-left text-white">Agent</h2>                                
                                <div className="flex items-center space-x-2">
                                    <div className="flex space-x-1">
                                        <div className="w-2 h-2 bg-black dark:bg-white rounded-full animate-bounce"></div>
                                        <div className="w-2 h-2 bg-black dark:bg-white rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                        <div className="w-2 h-2 bg-black dark:bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                    </div>
                                    <span className="text-xs text-gray-600 dark:text-gray-400 ml-2">AI is thinking...</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Input and Actions */}
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-white dark:bg-black border-t border-gray-300 dark:border-gray-700"
            >
                <div className="flex items-end space-x-3 max-w-4xl mx-auto">
                    <div className="flex-1 relative">
                        <input
                            id="chat-input"
                            ref={inputRef}
                            type="text"
                            placeholder={
                                isInputEnabled
                                    ? "Type your message..."
                                    : "Please wait..."
                            }
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="w-full px-4 py-3 pr-12 bg-white dark:bg-black
                                     text-black dark:text-white rounded-2xl
                                     placeholder-gray-500 dark:placeholder-gray-400
                                     focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-600
                                     disabled:opacity-50 disabled:cursor-not-allowed
                                     transition-all duration-200 shadow-sm
                                     border border-gray-300 dark:border-gray-700"
                            disabled={!isInputEnabled}
                        />
                        {newMessage.trim() && (
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2"
                            >
                                <span className="text-xs text-gray-500">
                                    {newMessage.length}
                                </span>
                            </motion.div>
                        )}
                    </div>
                    <motion.button
                        onClick={handleSendMessage}
                        disabled={!isInputEnabled || !newMessage.trim()}
                        className={`
                            p-3 rounded-2xl font-medium shadow-sm border
                            transition-all duration-200 flex items-center justify-center
                            ${isInputEnabled && newMessage.trim()
                                ? "bg-black dark:bg-white hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white text-white dark:text-black border border-gray-300 dark:border-gray-700"
                                : "bg-gray-200 dark:bg-gray-800 text-gray-400 border-gray-300 dark:border-gray-700 cursor-not-allowed"
                            }
                        `}
                        whileHover={isInputEnabled && newMessage.trim() ? { scale: 1.05 } : {}}
                        whileTap={isInputEnabled && newMessage.trim() ? { scale: 0.95 } : {}}
                        aria-label="Send message"
                    >
                        <BsSend className="w-5 h-5" />
                    </motion.button>
                </div>
            </motion.div>
        </div>
    );
}

export default ChatPage;
